'use client';

import { useEffect, useRef, useCallback, useState } from 'react';
import type { IllustrationForCanvas } from '@/lib/illustrationTypes';
import { fetchActiveIllustrations } from '@/lib/illustrationService';
import { fetchBackground } from '@/lib/illustrationService';

// ─── Flying creature detection ────────────────────────────────────────────────

const FLYING_KEYWORDS = ['butterfly', 'bee', 'bumblebee', 'chaffinch', 'nuthatch', 'owl', 'bird'];

function isFlying(label: string): boolean {
  const lower = label.toLowerCase();
  return FLYING_KEYWORDS.some(k => lower.includes(k));
}

// ─── Animation types ──────────────────────────────────────────────────────────

type AnimType =
  | 'drift'       // gentle wind drift (replaces old fall/float-up/drift-left/drift-right/bob/spin-drift)
  | 'fly-left'    // flying creature crossing left
  | 'fly-right'   // flying creature crossing right
  | 'landing'     // falls to grass, rests, fades
  | 'burst'       // burst mode outward drift
  | 'leaf-fall';  // DOWN arrow leaf fall

// ─── Active illustration ──────────────────────────────────────────────────────

interface ActiveIllus {
  id: number;
  img: HTMLImageElement;
  label: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rotation: number;
  anim: AnimType;
  alpha: number;
  born: number;
  lifetime: number;
  // Wind drift oscillation
  windPhaseX: number;   // sideways sine phase
  windPhaseY: number;   // up/down sine phase
  windPhaseR: number;   // rotation sine phase
  windAmpX: number;     // sideways amplitude px
  windAmpY: number;     // up/down amplitude px
  windAmpR: number;     // rotation amplitude rad
  windSpeedX: number;   // sideways oscillation speed
  windSpeedY: number;   // up/down oscillation speed
  windSpeedR: number;   // rotation oscillation speed
  // Flying creature
  flyBaseY: number;     // centre Y for sine arc
  flyArcAmp: number;    // arc amplitude
  flyArcFreq: number;   // arc frequency
  flyT: number;         // normalised time 0-1 for fly path
  flySpeed: number;     // px per frame
  flutterPhase: number; // wing flutter scale pulse phase
  // Landing
  landY: number;        // target Y for landing
  landState: 'falling' | 'resting' | 'fading';
  landRestStart: number;
  // Depth
  baseOpacity: number;  // 0.82 – 1.0
}

// ─── Pollen particle ─────────────────────────────────────────────────────────

interface Pollen {
  x: number;
  y: number;
  r: number;
  color: string;
  opacity: number;
  vx: number;
  vy: number;
  phase: number;
  phaseSpeed: number;
  ampX: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const NORMAL_LIFETIME = 9000;
const FADE_IN_MS = 700;
const FADE_OUT_MS = 1400;
const POLLEN_COUNT = 20;
const POLLEN_COLORS = ['#F5E6C8', '#EDD9A3', '#F2E4C4', '#F7EDD5'];
const BASE_UNIT = 90;

let uidCounter = 0;

// ─── Image cache ──────────────────────────────────────────────────────────────

const imageCache = new Map<string, HTMLImageElement>();

function loadImage(src: string): HTMLImageElement {
  if (imageCache.has(src)) return imageCache.get(src)!;
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = src;
  imageCache.set(src, img);
  return img;
}

// ─── Pollen helpers ───────────────────────────────────────────────────────────

function makePollen(w: number, h: number, startAnywhere = true): Pollen {
  return {
    x: Math.random() * w,
    y: startAnywhere ? Math.random() * h : h + 4,
    r: 1.5 + Math.random() * 1.5,
    color: POLLEN_COLORS[Math.floor(Math.random() * POLLEN_COLORS.length)],
    opacity: 0.15 + Math.random() * 0.15,
    vx: (Math.random() - 0.5) * 0.12,
    vy: -(0.08 + Math.random() * 0.12),
    phase: Math.random() * Math.PI * 2,
    phaseSpeed: 0.004 + Math.random() * 0.006,
    ampX: 0.3 + Math.random() * 0.5,
  };
}

// ─── Spawn helpers ────────────────────────────────────────────────────────────

function windParams() {
  return {
    windPhaseX: Math.random() * Math.PI * 2,
    windPhaseY: Math.random() * Math.PI * 2,
    windPhaseR: Math.random() * Math.PI * 2,
    windAmpX: 10 + Math.random() * 12,   // 10-22px sideways
    windAmpY: 4 + Math.random() * 8,     // 4-12px up/down
    windAmpR: (0.04 + Math.random() * 0.1), // ~2-8 deg
    windSpeedX: 0.0008 + Math.random() * 0.0008, // period ~4-8s at 60fps
    windSpeedY: 0.0006 + Math.random() * 0.0007,
    windSpeedR: 0.0005 + Math.random() * 0.0006,
  };
}

function spawnDrift(
  illus: IllustrationForCanvas,
  w: number,
  h: number,
  overrides?: Partial<ActiveIllus>
): ActiveIllus {
  const baseSize = illus.scale * BASE_UNIT;
  const variation = 0.9 + Math.random() * 0.2;
  const size = Math.round(baseSize * variation);
  const margin = size;

  // Random entry from any edge
  const edge = Math.floor(Math.random() * 4); // 0=top 1=bottom 2=left 3=right
  let x: number, y: number, vx: number, vy: number;
  switch (edge) {
    case 0: // top
      x = margin + Math.random() * (w - margin * 2);
      y = -size;
      vx = (Math.random() - 0.5) * 0.3;
      vy = 0.25 + Math.random() * 0.3;
      break;
    case 1: // bottom
      x = margin + Math.random() * (w - margin * 2);
      y = h + size;
      vx = (Math.random() - 0.5) * 0.25;
      vy = -(0.25 + Math.random() * 0.3);
      break;
    case 2: // left
      x = -size;
      y = h * 0.1 + Math.random() * h * 0.8;
      vx = 0.25 + Math.random() * 0.3;
      vy = (Math.random() - 0.5) * 0.15;
      break;
    default: // right
      x = w + size;
      y = h * 0.1 + Math.random() * h * 0.8;
      vx = -(0.25 + Math.random() * 0.3);
      vy = (Math.random() - 0.5) * 0.15;
      break;
  }

  const baseOpacity = 0.82 + Math.random() * 0.18;

  return {
    id: ++uidCounter,
    img: loadImage(illus.imageSrc),
    label: illus.label,
    x, y, vx, vy,
    size,
    rotation: (Math.random() - 0.5) * 0.3,
    anim: 'drift',
    alpha: 0,
    born: performance.now(),
    lifetime: NORMAL_LIFETIME + Math.random() * 4000,
    ...windParams(),
    flyBaseY: 0, flyArcAmp: 0, flyArcFreq: 0, flyT: 0, flySpeed: 0, flutterPhase: 0,
    landY: 0, landState: 'falling', landRestStart: 0,
    baseOpacity,
    ...overrides,
  };
}

function spawnFlying(
  illus: IllustrationForCanvas,
  w: number,
  h: number,
  direction: 'left' | 'right'
): ActiveIllus {
  const baseSize = illus.scale * BASE_UNIT;
  const size = Math.round(baseSize * (0.9 + Math.random() * 0.2));
  const baseY = h * 0.2 + Math.random() * h * 0.45;
  const speed = 0.6 + Math.random() * 0.5;
  let x = direction === 'right' ? -size : w + size;
  let vx = direction === 'right' ? speed : -speed;
  const baseOpacity = 0.85 + Math.random() * 0.15;

  return {
    id: ++uidCounter,
    img: loadImage(illus.imageSrc),
    label: illus.label,
    x,
    y: baseY,
    vx,
    vy: 0,
    size,
    rotation: 0,
    anim: direction === 'right' ? 'fly-right' : 'fly-left',
    alpha: 0,
    born: performance.now(),
    lifetime: Math.round((w + size * 2) / speed * (1000 / 60)) + 2000,
    ...windParams(),
    flyBaseY: baseY,
    flyArcAmp: 18 + Math.random() * 20,
    flyArcFreq: 0.003 + Math.random() * 0.002,
    flyT: 0,
    flySpeed: speed,
    flutterPhase: Math.random() * Math.PI * 2,
    landY: 0, landState: 'falling', landRestStart: 0,
    baseOpacity,
  };
}

function spawnLanding(
  illus: IllustrationForCanvas,
  w: number,
  h: number
): ActiveIllus {
  const baseSize = illus.scale * BASE_UNIT;
  const size = Math.round(baseSize * (0.9 + Math.random() * 0.2));
  const margin = size;
  let x = margin + Math.random() * (w - margin * 2);
  const landY = h * 0.78 + Math.random() * h * 0.1; // grass area bottom 20%
  const baseOpacity = 0.85 + Math.random() * 0.15;

  return {
    id: ++uidCounter,
    img: loadImage(illus.imageSrc),
    label: illus.label,
    x,
    y: -size,
    vx: (Math.random() - 0.5) * 0.2,
    vy: 0.3 + Math.random() * 0.25,
    size,
    rotation: (Math.random() - 0.5) * 0.2,
    anim: 'landing',
    alpha: 0,
    born: performance.now(),
    lifetime: 14000 + Math.random() * 3000, // long — landing + rest + fade
    ...windParams(),
    flyBaseY: 0, flyArcAmp: 0, flyArcFreq: 0, flyT: 0, flySpeed: 0, flutterPhase: 0,
    landY,
    landState: 'falling',
    landRestStart: 0,
    baseOpacity,
  };
}

function spawnBurst(
  illus: IllustrationForCanvas,
  cx: number,
  cy: number,
  angle: number,
  scaleMult: number
): ActiveIllus {
  const baseSize = illus.scale * BASE_UNIT;
  const size = Math.round(baseSize * scaleMult * (0.85 + Math.random() * 0.3));
  const speed = 0.4 + Math.random() * 0.5;
  let vx = Math.cos(angle) * speed;
  let vy = Math.sin(angle) * speed;
  const baseOpacity = 0.85 + Math.random() * 0.15;

  return {
    id: ++uidCounter,
    img: loadImage(illus.imageSrc),
    label: illus.label,
    x: cx + (Math.random() - 0.5) * 30,
    y: cy + (Math.random() - 0.5) * 30,
    vx, vy,
    size,
    rotation: (Math.random() - 0.5) * 0.4,
    anim: 'burst',
    alpha: 0,
    born: performance.now(),
    lifetime: 3500 + Math.random() * 1000,
    ...windParams(),
    flyBaseY: 0, flyArcAmp: 0, flyArcFreq: 0, flyT: 0, flySpeed: 0, flutterPhase: 0,
    landY: 0, landState: 'falling', landRestStart: 0,
    baseOpacity,
  };
}

function spawnLeafFall(
  illus: IllustrationForCanvas,
  w: number,
  h: number,
  index: number,
  total: number
): ActiveIllus {
  const baseSize = illus.scale * BASE_UNIT;
  const size = Math.round(baseSize * (0.8 + Math.random() * 0.3));
  let x = (w / (total + 1)) * (index + 1) + (Math.random() - 0.5) * 80;
  const baseOpacity = 0.85 + Math.random() * 0.15;

  return {
    id: ++uidCounter,
    img: loadImage(illus.imageSrc),
    label: illus.label,
    x,
    y: -size - Math.random() * 60,
    vx: (Math.random() - 0.5) * 0.3,
    vy: 0.3 + Math.random() * 0.3,
    size,
    rotation: (Math.random() - 0.5) * 0.5,
    anim: 'drift',
    alpha: 0,
    born: performance.now(),
    lifetime: 10000 + Math.random() * 3000,
    ...windParams(),
    flyBaseY: 0, flyArcAmp: 0, flyArcFreq: 0, flyT: 0, flySpeed: 0, flutterPhase: 0,
    landY: 0, landState: 'falling', landRestStart: 0,
    baseOpacity,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

interface IllustrationLayerProps {
  onAAADetected: () => void;
}

export default function IllustrationLayer({ onAAADetected }: IllustrationLayerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const itemsRef = useRef<ActiveIllus[]>([]);
  const pollenRef = useRef<Pollen[]>([]);
  const rafRef = useRef<number>(0);
  const keyTimesRef = useRef<number[]>([]);
  const illustrationsRef = useRef<IllustrationForCanvas[]>([]);
  const bgUrlRef = useRef<string | null>(null);
  const [bgUrl, setBgUrl] = useState<string | null>(null);
  const keyBufferRef = useRef<string[]>([]);

  // ── Load illustrations ───────────────────────────────────────────────────────
  useEffect(() => {
    fetchActiveIllustrations().then(assets => {
      const forCanvas: IllustrationForCanvas[] = assets.map(a => ({
        id: a.id,
        imageSrc: a.storageUrl,
        label: a.name,
        defaultSize: Math.round((a.scale ?? 1.0) * 90),
        scale: a.scale ?? 1.0,
      }));
      illustrationsRef.current = forCanvas;
      forCanvas.forEach(i => loadImage(i.imageSrc));
    });

    fetchBackground().then(bg => {
      if (bg) {
        setBgUrl(bg.storageUrl);
        bgUrlRef.current = bg.storageUrl;
      }
    });
  }, []);

  // ── Init pollen ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    pollenRef.current = Array.from({ length: POLLEN_COUNT }, () => makePollen(w, h, true));
  }, []);

  // ── Animation loop ───────────────────────────────────────────────────────────
  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const now = performance.now();
    const w = canvas.width;
    const h = canvas.height;

    ctx.clearRect(0, 0, w, h);

    // ── 1. Sunlight glow (drawn first, behind everything) ──────────────────────
    const glowX = w * 0.5;
    const glowY = h * 0.32;
    const glowR = w * 0.42;
    const glow = ctx.createRadialGradient(glowX, glowY, 0, glowX, glowY, glowR);
    glow.addColorStop(0, 'rgba(255,248,231,0.07)');
    glow.addColorStop(0.5, 'rgba(255,248,231,0.03)');
    glow.addColorStop(1, 'rgba(255,248,231,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, w, h);

    // ── 2. Pollen particles ────────────────────────────────────────────────────
    pollenRef.current.forEach(p => {
      p.phase += p.phaseSpeed;
      p.x += p.vx + Math.sin(p.phase) * p.ampX;
      p.y += p.vy;

      // Respawn at bottom when drifted off top
      if (p.y < -6) {
        const np = makePollen(w, h, false);
        p.x = np.x; p.y = np.y; p.r = np.r;
        p.color = np.color; p.opacity = np.opacity;
        p.vx = np.vx; p.vy = np.vy;
        p.phase = np.phase; p.phaseSpeed = np.phaseSpeed; p.ampX = np.ampX;
      }
      // Wrap horizontally
      if (p.x < -6) p.x = w + 4;
      if (p.x > w + 6) p.x = -4;

      ctx.save();
      ctx.globalAlpha = p.opacity;
      ctx.shadowBlur = 4;
      ctx.shadowColor = p.color;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    });

    // ── 3. Illustrations ───────────────────────────────────────────────────────
    itemsRef.current = itemsRef.current.filter(item => {
      const age = now - item.born;

      // ── Landing state machine ────────────────────────────────────────────────
      if (item.anim === 'landing') {
        const distToLand = item.landY - item.y;

        if (item.landState === 'falling') {
          // Decelerate as approaching land
          if (distToLand > 0 && distToLand < h * 0.15) {
            const decel = distToLand / (h * 0.15);
            item.vy = Math.max(0.05, item.vy * (0.97 + 0.03 * (1 - decel)));
          }
          if (item.y >= item.landY) {
            item.y = item.landY;
            item.vy = 0;
            item.vx = 0;
            item.landState = 'resting';
            item.landRestStart = now;
          }
        }

        if (item.landState === 'resting') {
          const restAge = now - item.landRestStart;
          if (restAge > 1800) {
            item.landState = 'fading';
          }
        }

        // Alpha for landing
        let alpha: number;
        if (age < FADE_IN_MS) {
          alpha = age / FADE_IN_MS;
        } else if (item.landState === 'fading') {
          const fadeAge = now - item.landRestStart - 1800;
          alpha = Math.max(0, 1 - fadeAge / FADE_OUT_MS);
          if (alpha <= 0) return false;
        } else {
          alpha = 1;
        }
        alpha = Math.max(0, Math.min(1, alpha)) * item.baseOpacity;

        // Gentle wind on falling
        if (item.landState === 'falling') {
          item.windPhaseX += item.windSpeedX * 60;
          item.windPhaseY += item.windSpeedY * 60;
          item.windPhaseR += item.windSpeedR * 60;
          item.x += item.vx + Math.sin(item.windPhaseX) * item.windAmpX * 0.015;
          item.y += item.vy;
          item.rotation = Math.sin(item.windPhaseR) * item.windAmpR;
        }

        if (item.img.complete && item.img.naturalWidth > 0) {
          ctx.save();
          ctx.globalAlpha = alpha;
          ctx.translate(item.x, item.y);
          ctx.rotate(item.rotation);
          ctx.drawImage(item.img, -item.size / 2, -item.size / 2, item.size, item.size);
          ctx.restore();
        }
        return true;
      }

      // ── Normal lifetime fade ─────────────────────────────────────────────────
      if (age > item.lifetime + FADE_OUT_MS) return false;

      let alpha: number;
      if (age < FADE_IN_MS) {
        alpha = age / FADE_IN_MS;
      } else if (age > item.lifetime) {
        alpha = 1 - (age - item.lifetime) / FADE_OUT_MS;
      } else {
        alpha = 1;
      }
      alpha = Math.max(0, Math.min(1, alpha)) * item.baseOpacity;

      // ── Flying creature path ─────────────────────────────────────────────────
      if (item.anim === 'fly-left' || item.anim === 'fly-right') {
        item.x += item.vx;
        item.flyT += Math.abs(item.vx) / (canvasRef.current?.width ?? w);
        item.y = item.flyBaseY + Math.sin(item.flyT * Math.PI * 4 * item.flyArcFreq * 300) * item.flyArcAmp;
        item.flutterPhase += 0.07; // ~0.8s period at 60fps
        const flutter = 1 + Math.sin(item.flutterPhase) * 0.05;
        const drawSize = item.size * flutter;
        // Gentle tilt in direction of travel
        item.rotation = (item.anim === 'fly-right' ? 1 : -1) * 0.06 + Math.sin(item.flutterPhase * 0.5) * 0.04;

        if (item.img.complete && item.img.naturalWidth > 0) {
          ctx.save();
          ctx.globalAlpha = alpha;
          ctx.translate(item.x, item.y);
          ctx.rotate(item.rotation);
          ctx.drawImage(item.img, -drawSize / 2, -drawSize / 2, drawSize, drawSize);
          ctx.restore();
        }
        return true;
      }

      // ── Drift / burst / leaf-fall ────────────────────────────────────────────
      item.windPhaseX += item.windSpeedX * 60;
      item.windPhaseY += item.windSpeedY * 60;
      item.windPhaseR += item.windSpeedR * 60;

      item.x += item.vx + Math.sin(item.windPhaseX) * item.windAmpX * 0.015;
      item.y += item.vy + Math.sin(item.windPhaseY) * item.windAmpY * 0.012;
      item.rotation = Math.sin(item.windPhaseR) * item.windAmpR;

      if (item.img.complete && item.img.naturalWidth > 0) {
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(item.x, item.y);
        ctx.rotate(item.rotation);
        ctx.drawImage(item.img, -item.size / 2, -item.size / 2, item.size, item.size);
        ctx.restore();
      }

      return true;
    });

    rafRef.current = requestAnimationFrame(animate);
  }, []);

  // ── Keyboard handler ─────────────────────────────────────────────────────────
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') return;
    if (e.ctrlKey || e.metaKey || e.altKey) {
      e.preventDefault();
      return;
    }

    // Allow arrow keys, space, enter, and regular character keys
    const isSpecial = ['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp', ' ', 'Enter'].includes(e.key);
    if (!isSpecial && e.key.length > 1) return;

    // AAA detection
    const key = e.key.toUpperCase();
    keyBufferRef.current.push(key);
    if (keyBufferRef.current.length > 3) keyBufferRef.current = keyBufferRef.current.slice(-3);
    if (
      keyBufferRef.current.length === 3 &&
      keyBufferRef.current[0] === 'A' &&
      keyBufferRef.current[1] === 'A' &&
      keyBufferRef.current[2] === 'A'
    ) {
      keyBufferRef.current = [];
      onAAADetected();
      return;
    }

    // Do not intercept keys when user is typing in an input
    const active = document.activeElement;
    const tag = active?.tagName?.toLowerCase();
    if (tag === 'input' || tag === 'textarea' || (active as HTMLElement)?.isContentEditable) return;

    e.preventDefault();

    if (illustrationsRef.current.length === 0) return;

    const now = performance.now();
    const w = canvasRef.current?.width ?? window.innerWidth;
    const h = canvasRef.current?.height ?? window.innerHeight;
    const pool = illustrationsRef.current;

    // ── Special key effects ──────────────────────────────────────────────────
    if (e.key === ' ') {
      // Flower burst: 5-7 illustrations from centre
      const count = 5 + Math.floor(Math.random() * 3);
      const cx = w * 0.5;
      const cy = h * 0.5;
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 / count) * i + (Math.random() - 0.5) * 0.5;
        const illus = pool[Math.floor(Math.random() * pool.length)];
        const delay = i * 80;
        setTimeout(() => {
          itemsRef.current.push(spawnBurst(illus, cx, cy, angle, 1.0));
        }, delay);
      }
      return;
    }

    if (e.key === 'Enter') {
      // Cluster: 4-6 near centre
      const count = 4 + Math.floor(Math.random() * 3);
      const cx = w * 0.4 + Math.random() * w * 0.2;
      const cy = h * 0.35 + Math.random() * h * 0.3;
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const illus = pool[Math.floor(Math.random() * pool.length)];
        const delay = i * 120;
        setTimeout(() => {
          let item = spawnBurst(illus, cx, cy, angle, 1.0);
          item.lifetime = 7000 + Math.random() * 1500;
          itemsRef.current.push(item);
        }, delay);
      }
      return;
    }

    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      // Flying creature crosses screen
      const dir = e.key === 'ArrowLeft' ? 'left' : 'right';
      const flyingPool = pool.filter(p => isFlying(p.label));
      const src = flyingPool.length > 0 ? flyingPool[Math.floor(Math.random() * flyingPool.length)] : pool[Math.floor(Math.random() * pool.length)];
      itemsRef.current.push(spawnFlying(src, w, h, dir));
      return;
    }

    if (e.key === 'ArrowDown') {
      // Leaves drifting down from top
      const count = 3 + Math.floor(Math.random() * 3);
      for (let i = 0; i < count; i++) {
        const illus = pool[Math.floor(Math.random() * pool.length)];
        const delay = i * 200 + Math.random() * 100;
        setTimeout(() => {
          itemsRef.current.push(spawnLeafFall(illus, w, h, i, count));
        }, delay);
      }
      return;
    }

    // ── Burst mode detection ─────────────────────────────────────────────────
    keyTimesRef.current.push(now);
    keyTimesRef.current = keyTimesRef.current.filter(t => now - t < 2000);
    const recentCount = keyTimesRef.current.length;
    const isBurst = recentCount >= 5;

    if (isBurst) {
      // Burst mode: 6-8 small illustrations from random position
      const count = 6 + Math.floor(Math.random() * 3);
      const cx = w * 0.2 + Math.random() * w * 0.6;
      const cy = h * 0.2 + Math.random() * h * 0.6;
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 / count) * i + (Math.random() - 0.5) * 0.6;
        const illus = pool[Math.floor(Math.random() * pool.length)];
        const delay = i * 60 + Math.random() * 40;
        setTimeout(() => {
          itemsRef.current.push(spawnBurst(illus, cx, cy, angle, 0.7));
        }, delay);
      }
      return;
    }

    // ── Normal spawn ─────────────────────────────────────────────────────────
    const illus = pool[Math.floor(Math.random() * pool.length)];
    const flying = isFlying(illus.label);

    if (flying) {
      // Flying creatures use curved path
      const dir = Math.random() < 0.5 ? 'left' : 'right';
      itemsRef.current.push(spawnFlying(illus, w, h, dir));
    } else {
      // 30% chance of landing behaviour
      const useLanding = Math.random() < 0.3;
      if (useLanding) {
        itemsRef.current.push(spawnLanding(illus, w, h));
      } else {
        itemsRef.current.push(spawnDrift(illus, w, h));
      }
    }
  }, [onAAADetected]);

  // ── Mouse click handler ──────────────────────────────────────────────────────
  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (illustrationsRef.current.length === 0) return;
    const pool = illustrationsRef.current;
    const w = canvasRef.current?.width ?? window.innerWidth;
    const h = canvasRef.current?.height ?? window.innerHeight;
    const cx = e.clientX;
    const cy = e.clientY;

    const count = 1 + Math.floor(Math.random() * 2); // 1 or 2
    for (let i = 0; i < count; i++) {
      const illus = pool[Math.floor(Math.random() * pool.length)];
      const delay = i * 120;
      setTimeout(() => {
        const flying = isFlying(illus.label);
        let item: ActiveIllus;
        if (flying) {
          const dir = Math.random() < 0.5 ? 'left' : 'right';
          item = spawnFlying(illus, w, h, dir);
        } else {
          const useLanding = Math.random() < 0.3;
          if (useLanding) {
            item = spawnLanding(illus, w, h);
          } else {
            item = spawnDrift(illus, w, h);
          }
          // Override starting position to the click point
          item.x = cx + (Math.random() - 0.5) * 20;
          item.y = cy + (Math.random() - 0.5) * 20;
        }
        itemsRef.current.push(item);
      }, delay);
    }
  }, []);

  // ── Setup ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleMouseDown);
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleMouseDown);
      cancelAnimationFrame(rafRef.current);
    };
  }, [animate, handleKeyDown, handleMouseDown]);

  return (
    <>
      {/* Background layer */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1,
          pointerEvents: 'none',
          overflow: 'hidden',
          background: bgUrl ? undefined : '#f5ede0',
        }}
      >
        {bgUrl && (
          <img
            src={bgUrl}
            alt=""
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center center',
              display: 'block',
            }}
          />
        )}
      </div>
      {/* Illustration canvas */}
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 5,
          mixBlendMode: 'multiply',
        }}
      />
    </>
  );
}